import * as React from 'react';
import DefaultLayout from '../../../../components/DefaultLayout';
import Section from '@hero/ui-kit/layout/Section';
import BackButton from '../../../../components/BackButton';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useGetCRMProspectDetails from '../../api/useGetCRMProspectDetails';
import InlineLoader from '../../../../components/InlineLoader';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import DeactivateModal from './modals/DeactivateModal';
import PostponeSLATimerModal from './modals/PostponeSLATimer';
import SendCommunicationModal from './modals/SendCommunicationModal';
import CallDispositionModal from './modals/CallDispositionModal';
import H from '@hero/ui-kit/typography/H';
import AddNoteForm from './AddNoteForm';
import LogList from './LogList';
import SLATimer from '../SLATimer';
import useProspectAction from '../../api/useProspectAction';
import getStatusLabel from '../utils/getStatusLabel';
import useInterval from '../../../../hooks/useInterval';
import useBack from '../../../../hooks/useBack';
import StickyNote from './StickyNote';
import UpdatePhoneModal from './modals/UpdatePhoneModal';
import AlertBadge from './AlertBadge';
import getShowAlertBadge from '../utils/getShowAlertBadge';
import getFormattedLabel from '../utils/getFormattedLabel';
import Link from '../../../../components/Link';
import SmsLogList from './SmsLogList';
import ReplySmsForm from './ReplySmsForm';

const DetailRow: React.FC<{ children: React.ReactNode; gap?: string }> = ({
    children,
    gap = '3rem'
}) => <div style={{ display: 'flex', alignItems: 'center', columnGap: gap }}>{children}</div>;

const CRMUsersDetails: React.FC = () => {
    const navigate = useNavigate();
    const [showDeactivate, setShowDeactivate] = React.useState(false);
    const [showPostponeSLA, setShowPostponeSLA] = React.useState(false);
    const [showSendCommunication, setShowSendCommunication] = React.useState(false);
    const [showCallDisposition, setShowCallDisposition] = React.useState(false);
    const [showUpdatePhone, setShowUpdatePhone] = React.useState(false);
    const [log, setLog] = React.useState('prospect');

    const { mutateAsync: prospectAction } = useProspectAction();

    const { prospect_id } = useParams<{ prospect_id: string }>();

    const [searchParams] = useSearchParams();

    const force_view = searchParams.get('force_view');
    const force_sms_log = searchParams.get('force_sms_log');

    // call release lock on unmount
    React.useEffect(() => {
        return () => {
            const releaseLock = () => {
                if (prospect_id) {
                    prospectAction({ prospect_id, action: 'release_lock' }).catch(() => {});
                }
            };
            releaseLock();
        };
    }, [prospect_id, prospectAction]);

    useInterval(() => {
        if (prospect_id) {
            prospectAction({ prospect_id, action: 'refresh_lock' });
        }
    }, 60000);

    const back = useBack();

    const handleNavigate = () => {
        back('/crm/list');
    };

    const {
        data: prospectResponse,
        isFetching: isProspectFetching,
        error
    } = useGetCRMProspectDetails({
        prospect_id: prospect_id || '',
        ...(force_view === 'true' && { force_acquire: true })
    });

    const errorMessages = error?.response?.data?.errors || [];

    const prospect = prospectResponse?.data.prospect;

    const crmContact = prospectResponse?.data.prospect.crm_contact;

    const communication = prospectResponse?.data.communication;

    const isProspectActive = prospect?.is_active;

    const logs = log === 'prospect' ? prospect?.log || [] : prospect?.crm_contact.log || [];

    const smsLogs = prospect?.crm_contact?.sms_log || [];

    React.useEffect(() => {
        if (smsLogs.length || force_sms_log === 'true') {
            setLog('sms');
        }
    }, [smsLogs, force_sms_log]);

    const showBadgeAlert = getShowAlertBadge(prospect);

    if (errorMessages.length) {
        const isLockedByAnotherAgent = errorMessages.includes('prospect_locked_by_another_agent');

        return (
            <DefaultLayout>
                <Section
                    subtheme={['regular', 'background']}
                    paddingVertical="small"
                    paddingHorizontal="regular"
                >
                    {isLockedByAnotherAgent ? (
                        <>
                            <P centered strong size="large">
                                Prospect is locked by another agent
                            </P>

                            <Button
                                size="small"
                                onClick={() => {
                                    navigate(`/crm/${prospect_id}/details?force_view=true`);
                                    location.reload();
                                }}
                            >
                                Override lock and view
                            </Button>

                            <Button
                                variant="outlineSecondary"
                                size="small"
                                onClick={() => back('/crm/list')}
                            >
                                Back to CRM list
                            </Button>
                        </>
                    ) : (
                        <>
                            <P centered strong size="large">
                                Error fetching prospect details
                            </P>
                            {errorMessages.length > 0 && (
                                <ul>
                                    {errorMessages.map((message, index) => (
                                        <li key={index}>{message}</li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </Section>
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout>
            <Section
                subtheme={['regular', 'background']}
                paddingVertical="small"
                paddingHorizontal="regular"
            >
                {isProspectFetching ? (
                    <InlineLoader />
                ) : (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <DetailRow gap="3rem">
                                <BackButton onBack={handleNavigate} alt="back to list" />

                                {prospect ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            columnGap: '1rem'
                                        }}
                                    >
                                        {showBadgeAlert ? (
                                            <AlertBadge message="Prospect is rejected or expired in reelection" />
                                        ) : null}
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <H
                                                role="h6"
                                                noDefaultMargin
                                            >{`${getStatusLabel(prospect)} - ${prospect?.crm_contact.first_name} ${prospect?.crm_contact.last_name}`}</H>
                                        </div>
                                    </div>
                                ) : null}

                                <Button
                                    disabled={!isProspectActive}
                                    size="small"
                                    onClick={() => setShowDeactivate(true)}
                                >
                                    Deactivate
                                </Button>

                                {showBadgeAlert && prospect?.member_id ? (
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            navigate(
                                                `/organizations/member/${prospect.member_id}?route=cancellation`
                                            )
                                        }
                                    >
                                        Cancel
                                    </Button>
                                ) : null}

                                {!isProspectActive ? (
                                    <P noDefaultMargin strong>
                                        ⚠️ Prospect Inactive!
                                    </P>
                                ) : null}
                            </DetailRow>
                            {crmContact?.sticky_note ? (
                                <StickyNote note={crmContact.sticky_note} />
                            ) : null}
                        </div>
                        {prospect?.last_outreached_by && prospect?.last_outreached_at ? (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{ fontSize: '10px', fontStyle: 'italic' }}
                                strong
                            >
                                LAST OUTREACHED: {prospect.last_outreached_by} at{' '}
                                {prospect.last_outreached_at}
                            </P>
                        ) : null}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'start',
                                gap: '10rem',
                                marginTop: '3rem',
                                marginBottom: '5rem'
                            }}
                        >
                            <div
                                style={{
                                    flexBasis: '40%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    rowGap: '2rem'
                                }}
                            >
                                {prospect?.created_at ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Created At
                                        </P>
                                        <P noDefaultMargin>{prospect?.created_at || 'N/A'}</P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.rejected_reason ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Rejected Reason
                                        </P>
                                        <P noDefaultMargin>
                                            {getFormattedLabel(prospect?.rejected_reason)}
                                        </P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.block_device_at ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Block Device At
                                        </P>
                                        <P noDefaultMargin>{prospect?.block_device_at || 'N/A'}</P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.rtm_reelection_scenario ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            RTM Reelection Scenario
                                        </P>
                                        <P noDefaultMargin>
                                            {getFormattedLabel(prospect.rtm_reelection_scenario)}
                                        </P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.provider_hero_organization ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Provider Hero Organization
                                        </P>
                                        <P noDefaultMargin>
                                            {getFormattedLabel(prospect.provider_hero_organization)}
                                        </P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.discharged_at ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Discharged At
                                        </P>
                                        <P noDefaultMargin>{prospect?.discharged_at || 'N/A'}</P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.discharged_reason ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Discharged Reason
                                        </P>
                                        <P noDefaultMargin>
                                            {prospect?.discharged_reason || 'N/A'}
                                        </P>
                                    </DetailRow>
                                ) : null}
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        Is existing member?
                                    </P>
                                    <P
                                        noDefaultMargin
                                        styles={{
                                            ...(prospect?.is_existing_member && {
                                                color: '#f26c3a'
                                            })
                                        }}
                                    >
                                        {prospect?.is_existing_member ? 'YES' : 'NO'}
                                    </P>
                                    {prospect?.is_existing_member && prospect.member_id ? (
                                        <Link to={`/organizations/member/${prospect.member_id}`}>
                                            {`Member ID: ${prospect.member_id}`}
                                        </Link>
                                    ) : null}
                                </DetailRow>
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        Is caregiver?
                                    </P>
                                    <P
                                        noDefaultMargin
                                        styles={{
                                            ...(prospect?.is_caregiver && {
                                                color: '#f26c3a'
                                            })
                                        }}
                                    >
                                        {prospect?.is_caregiver ? 'YES' : 'NO'}
                                    </P>
                                </DetailRow>
                                {prospect?.rtm_2_d2c_coupon ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Coupon code for RTM to D2C
                                        </P>
                                        <P noDefaultMargin>{prospect?.rtm_2_d2c_coupon || 'N/A'}</P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.current_hero_organization ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Current Hero Organization
                                        </P>
                                        <P noDefaultMargin>
                                            {prospect?.current_hero_organization || 'N/A'}
                                        </P>
                                    </DetailRow>
                                ) : null}
                                {prospect?.insurance_discovery ? (
                                    <DetailRow>
                                        <P strong size="large" noDefaultMargin>
                                            Insurance Discovery
                                        </P>
                                        <P
                                            noDefaultMargin
                                            styles={{
                                                textTransform: 'uppercase',
                                                ...(prospect?.insurance_discovery ===
                                                    'eligible' && {
                                                    color: '#f26c3a'
                                                })
                                            }}
                                            strong
                                        >
                                            {getFormattedLabel(prospect?.insurance_discovery)}
                                        </P>
                                    </DetailRow>
                                ) : null}
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        Email
                                    </P>
                                    <P noDefaultMargin>{prospect?.crm_contact.email || 'N/A'}</P>
                                </DetailRow>
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        Phone
                                    </P>
                                    <P noDefaultMargin>{prospect?.crm_contact.phone || 'N/A'}</P>
                                    <Button
                                        disabled={!isProspectActive}
                                        size="small"
                                        onClick={() => setShowUpdatePhone(true)}
                                    >
                                        Update Phone
                                    </Button>
                                </DetailRow>
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        SLA Timer
                                    </P>
                                    {prospect ? <SLATimer prospect={prospect} /> : null}
                                    <Button
                                        disabled={!isProspectActive}
                                        size="small"
                                        onClick={() => setShowPostponeSLA(true)}
                                    >
                                        Postpone SLA
                                    </Button>
                                </DetailRow>
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        Communication
                                    </P>
                                    <Button
                                        size="small"
                                        onClick={() => setShowSendCommunication(true)}
                                    >
                                        Send communication
                                    </Button>
                                </DetailRow>
                                <DetailRow>
                                    <P strong size="large" noDefaultMargin>
                                        Call Disposition
                                    </P>
                                    <Button
                                        disabled={!isProspectActive}
                                        size="small"
                                        onClick={() => setShowCallDisposition(true)}
                                    >
                                        Log Call Disposition
                                    </Button>
                                </DetailRow>
                                <div style={{ marginTop: '2.4rem' }}>
                                    {prospect_id ? (
                                        <AddNoteForm
                                            disabled={!isProspectActive}
                                            prospectId={prospect_id}
                                        />
                                    ) : null}
                                </div>
                            </div>

                            <div
                                style={{
                                    flexBasis: '50%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2rem'
                                }}
                            >
                                <div style={{ display: 'flex', gap: '4rem', alignItems: 'center' }}>
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            border: 'none',
                                            background: 'none',
                                            padding: '1rem',
                                            fontSize: '1.6rem',
                                            borderBottom: `2px solid ${log === 'prospect' ? '#f26c3a' : '#e0e0e0'}`
                                        }}
                                        onClick={() => setLog('prospect')}
                                    >
                                        Prospect Logs
                                    </button>
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            border: 'none',
                                            background: 'none',
                                            padding: '1rem',
                                            fontSize: '1.6rem',
                                            borderBottom: `2px solid ${log === 'sms' ? '#f26c3a' : '#e0e0e0'}`
                                        }}
                                        onClick={() => setLog('sms')}
                                    >
                                        SMS Logs
                                    </button>
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            border: 'none',
                                            background: 'none',
                                            padding: '1rem',
                                            fontSize: '1.6rem',
                                            borderBottom: `2px solid ${log === 'contact' ? '#f26c3a' : '#e0e0e0'}`
                                        }}
                                        onClick={() => setLog('contact')}
                                    >
                                        Contact Logs
                                    </button>
                                </div>
                                {prospect_id && log === 'sms' ? (
                                    <ReplySmsForm
                                        prospectId={prospect_id}
                                        disabled={!isProspectActive}
                                    />
                                ) : null}
                                <div
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        padding: '2.4rem',
                                        borderRadius: '12px'
                                    }}
                                >
                                    <>
                                        {log === 'sms' ? (
                                            <SmsLogList logs={smsLogs} />
                                        ) : logs.length ? (
                                            <LogList logs={logs} />
                                        ) : (
                                            <P noDefaultMargin>No Logs for Prospect</P>
                                        )}
                                    </>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Section>
            {prospect_id ? (
                <>
                    <DeactivateModal
                        externalControls={[showDeactivate, setShowDeactivate]}
                        prospectId={prospect_id}
                    />
                    <PostponeSLATimerModal
                        externalControls={[showPostponeSLA, setShowPostponeSLA]}
                        prospectId={prospect_id}
                    />
                    <SendCommunicationModal
                        externalControls={[showSendCommunication, setShowSendCommunication]}
                        prospectId={prospect_id}
                        communication={communication}
                    />
                    <CallDispositionModal
                        externalControls={[showCallDisposition, setShowCallDisposition]}
                        prospectId={prospect_id}
                    />
                    <UpdatePhoneModal
                        externalControls={[showUpdatePhone, setShowUpdatePhone]}
                        prospectId={prospect_id}
                    />
                </>
            ) : null}
        </DefaultLayout>
    );
};

export default CRMUsersDetails;
